import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment-timezone';
import { TimeEntry } from '../types';
import { TimezoneService } from '../services/timezoneService';
import {
  buildEmployeeTimeSheetCsvExport,
  buildEmployeeTimeSheetExcelExport,
  buildEmployeeTimeSheetPdfExport,
} from './employeeTimeSheetExport';
import { getApprovedAbsenceDisplayLabel } from '../constants/absenceReasons';

function formatAbsenceTypeForExport(absence: { absenceType?: string | null; reason?: string | null }) {
  return getApprovedAbsenceDisplayLabel({
    absenceType: absence.absenceType,
    reason: absence.reason,
  });
}

export interface ExportData {
  data: TimeEntry[];
  filters: {
    startDate?: string;
    endDate?: string;
    userId?: string;
    departmentId?: string;
    campaignId?: string;
    entryType?: string;
  };
  reportTitle: string;
}

export interface DailySummaryExportData {
  data: any[];
  filters: {
    startDate?: string;
    endDate?: string;
    departmentId?: string;
    campaignId?: string;
  };
  reportTitle: string;
}

export interface CampaignPerformanceExportData {
  data: any[];
  filters: {
    startDate?: string;
    endDate?: string;
    campaignId?: string;
  };
  reportTitle: string;
}

export interface EmployeeAttendanceExportData {
  data: any[];
  filters: {
    startDate?: string;
    endDate?: string;
    userId?: string;
    departmentId?: string;
    campaignId?: string;
  };
  reportTitle: string;
}

export interface AbsenceExportData {
  data: any[];
  filters: {
    startDate?: string;
    endDate?: string;
    userId?: string;
    departmentId?: string;
    absenceType?: string;
    status?: string;
  };
  reportTitle: string;
}

// Helper functions
const formatEntryType = (entryType: string) => {
  return entryType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const formatTimestamp = (timestamp: string) => {
  return TimezoneService.formatDateTimeConsistent(timestamp, TimezoneService.getUserTimezone());
};

const getDateString = () => {
  const now = new Date();
  return now.toISOString().split('T')[0]; // YYYY-MM-DD format
};

export const exportToCSV = (exportData: ExportData) => {
  try {
    const { data, reportTitle } = exportData;
  
    // Prepare CSV headers
    const headers = [
      'Employee ID',
      'Employee Name',
      'Role',
      'Department',
      'Campaign',
      'Entry Type',
      'Timestamp',
      'Notes'
    ];
    
    // Prepare CSV data
    const csvData = data.map(entry => [
      (entry as any).user?.employeeId || '',
      `${(entry as any).user?.firstName || ''} ${(entry as any).user?.lastName || ''}`.trim(),
      (entry as any).user?.role || '',
      (entry as any).user?.department?.name || '',
      (entry as any).campaign?.name || '',
      formatEntryType(entry.entryType),
      formatTimestamp(entry.timestamp),
      entry.notes || ''
    ]);
    
    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${reportTitle}_${getDateString()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    console.log('✅ CSV export successful');
  } catch (error) {
    console.error('❌ CSV export failed:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    alert(`Failed to export CSV: ${errorMessage}`);
  }
};

export const exportToExcel = (exportData: ExportData) => {
  try {
    const { data, reportTitle } = exportData;
  
    // Prepare data for Excel
    const excelData = data.map(entry => ({
      'Employee ID': (entry as any).user?.employeeId || '',
      'Employee Name': `${(entry as any).user?.firstName || ''} ${(entry as any).user?.lastName || ''}`.trim(),
      'Role': (entry as any).user?.role || '',
      'Department': (entry as any).user?.department?.name || '',
      'Campaign': (entry as any).campaign?.name || '',
      'Entry Type': formatEntryType(entry.entryType),
      'Timestamp': formatTimestamp(entry.timestamp),
      'Notes': entry.notes || ''
    }));
    
    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);
    
    // Set column widths
    const colWidths = [
      { wch: 12 }, // Employee ID
      { wch: 20 }, // Employee Name
      { wch: 15 }, // Role
      { wch: 20 }, // Department
      { wch: 20 }, // Campaign
      { wch: 15 }, // Entry Type
      { wch: 20 }, // Timestamp
      { wch: 30 }  // Notes
    ];
    ws['!cols'] = colWidths;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Time Records');
    
    // Generate and download file
    XLSX.writeFile(wb, `${reportTitle}_${getDateString()}.xlsx`);
    
    console.log('✅ Excel export successful');
  } catch (error) {
    console.error('❌ Excel export failed:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    alert(`Failed to export Excel: ${errorMessage}`);
  }
};

export const exportToPDF = (exportData: ExportData) => {
  try {
    console.log('🔄 Starting PDF export...', exportData);
    
    const { data, filters, reportTitle } = exportData;
    
    // Create new PDF document
    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation
    console.log('📄 PDF document created');
  
    // Add title
    doc.setFontSize(16);
    doc.text(reportTitle, 14, 15);
    console.log('📝 Title added');
    
    // Add filters info
    doc.setFontSize(10);
    let yPosition = 25;
    
    if (filters.startDate || filters.endDate) {
      const dateRange = `${filters.startDate || 'Start'} to ${filters.endDate || 'End'}`;
      doc.text(`Date Range: ${dateRange}`, 14, yPosition);
      yPosition += 5;
    }
    
    if (filters.entryType) {
      doc.text(`Entry Type: ${formatEntryType(filters.entryType)}`, 14, yPosition);
      yPosition += 5;
    }
    
    doc.text(`Total Records: ${data.length}`, 14, yPosition);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, yPosition + 5);
    console.log('📊 Filter info added');
    
    // Prepare table data
    const tableData = data.map(entry => [
      (entry as any).user?.employeeId || '',
      `${(entry as any).user?.firstName || ''} ${(entry as any).user?.lastName || ''}`.trim(),
      (entry as any).user?.role || '',
      (entry as any).user?.department?.name || '',
      (entry as any).campaign?.name || '',
      formatEntryType(entry.entryType),
      formatTimestamp(entry.timestamp),
      entry.notes || ''
    ]);
    console.log('📋 Table data prepared:', tableData.length, 'rows');
    
    // Add table
    console.log('🔄 Adding table to PDF...');
    autoTable(doc, {
      head: [['Employee ID', 'Employee Name', 'Role', 'Department', 'Campaign', 'Entry Type', 'Timestamp', 'Notes']],
      body: tableData,
      startY: yPosition + 15,
      styles: {
        fontSize: 8,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { cellWidth: 15 }, // Employee ID
        1: { cellWidth: 25 }, // Employee Name
        2: { cellWidth: 15 }, // Role
        3: { cellWidth: 20 }, // Department
        4: { cellWidth: 20 }, // Campaign
        5: { cellWidth: 15 }, // Entry Type
        6: { cellWidth: 25 }, // Timestamp
        7: { cellWidth: 30 }, // Notes
      },
      margin: { left: 14, right: 14 },
    });
    console.log('✅ Table added successfully');
    
    // Save the PDF
    const fileName = `${reportTitle}_${getDateString()}.pdf`;
    console.log('💾 Saving PDF as:', fileName);
    doc.save(fileName);
    
    console.log('✅ PDF export successful');
  } catch (error) {
    console.error('❌ PDF export failed:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    console.error('Error details:', {
      message: errorMessage,
      stack: error instanceof Error ? error.stack : undefined,
      name: error instanceof Error ? error.name : undefined
    });
    alert(`Failed to export PDF: ${errorMessage}`);
  }
};

// Daily Summary Export Functions
export const exportDailySummaryToCSV = (data: any[], filters: any) => {
  try {
    const headers = [
      'Date',
      'Employee Name',
      'Department',
      'Campaign',
      'Clock In',
      'Clock Out',
      'Work Time (h)',
      'Break Time (m)',
      'Lunch Time (m)'
    ];
    
    const csvData = data.map(entry => {
      // Handle user name - could be nested in user object or flat
      let employeeName = '';
      if (entry.user) {
        if (typeof entry.user === 'string') {
          employeeName = entry.user;
        } else {
          employeeName = `${entry.user.firstName || ''} ${entry.user.lastName || ''}`.trim();
        }
      } else if (entry.employeeName) {
        employeeName = entry.employeeName;
      }
      
      // Handle department - could be nested or flat
      let department = 'N/A';
      if (entry.department) {
        department = typeof entry.department === 'string' ? entry.department : entry.department.name || 'N/A';
      } else if (entry.user?.department) {
        department = typeof entry.user.department === 'string' ? entry.user.department : entry.user.department.name || 'N/A';
      }
      
      // Handle campaign name - could be object or string
      let campaignName = 'N/A';
      if (entry.campaign) {
        if (typeof entry.campaign === 'string') {
          campaignName = entry.campaign;
        } else if (entry.campaign.name) {
          campaignName = entry.campaign.name;
        }
      }
      
      // Use formatted times if available, otherwise format raw timestamps
      let clockIn = entry.clockInFormatted || 'N/A';
      if (!clockIn || clockIn === 'N/A') {
        if (entry.clockIn) {
          clockIn = moment(entry.clockIn).format('hh:mm A');
        }
      }
      
      let clockOut = entry.clockOutFormatted || 'N/A';
      if (!clockOut || clockOut === 'N/A') {
        if (entry.clockOut) {
          clockOut = moment(entry.clockOut).format('hh:mm A');
        }
      }
      
      // Use totalWorkTime, fallback to totalHours
      const workTime = entry.totalWorkTime || entry.totalHours || 0;
      
      return [
        entry.date || '',
        employeeName || 'N/A',
        department,
        campaignName,
        clockIn,
        clockOut,
        workTime,
        entry.totalBreakTime || 0,
        entry.totalLunchTime || 0
      ];
    });
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Daily_Summary_Report_${getDateString()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    console.log('✅ Daily Summary CSV export successful');
  } catch (error) {
    console.error('❌ Daily Summary CSV export failed:', error);
    alert(`Failed to export CSV: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const exportDailySummaryToExcel = (data: any[], filters: any) => {
  try {
    const excelData = data.map(entry => {
      // Handle user name - could be nested in user object or flat
      let employeeName = '';
      if (entry.user) {
        if (typeof entry.user === 'string') {
          employeeName = entry.user;
        } else {
          employeeName = `${entry.user.firstName || ''} ${entry.user.lastName || ''}`.trim();
        }
      } else if (entry.employeeName) {
        employeeName = entry.employeeName;
      }
      
      // Handle department - could be nested or flat
      let department = 'N/A';
      if (entry.department) {
        department = typeof entry.department === 'string' ? entry.department : entry.department.name || 'N/A';
      } else if (entry.user?.department) {
        department = typeof entry.user.department === 'string' ? entry.user.department : entry.user.department.name || 'N/A';
      }
      
      // Handle campaign name - could be object or string
      let campaignName = 'N/A';
      if (entry.campaign) {
        if (typeof entry.campaign === 'string') {
          campaignName = entry.campaign;
        } else if (entry.campaign.name) {
          campaignName = entry.campaign.name;
        }
      }
      
      // Use formatted times if available, otherwise format raw timestamps
      let clockIn = entry.clockInFormatted || 'N/A';
      if (!clockIn || clockIn === 'N/A') {
        if (entry.clockIn) {
          clockIn = moment(entry.clockIn).format('hh:mm A');
        }
      }
      
      let clockOut = entry.clockOutFormatted || 'N/A';
      if (!clockOut || clockOut === 'N/A') {
        if (entry.clockOut) {
          clockOut = moment(entry.clockOut).format('hh:mm A');
        }
      }
      
      // Use totalWorkTime, fallback to totalHours
      const workTime = entry.totalWorkTime || entry.totalHours || 0;
      
      return {
        'Date': entry.date || '',
        'Employee Name': employeeName || 'N/A',
        'Department': department,
        'Campaign': campaignName,
        'Clock In': clockIn,
        'Clock Out': clockOut,
        'Work Time (h)': workTime,
        'Break Time (m)': entry.totalBreakTime || 0,
        'Lunch Time (m)': entry.totalLunchTime || 0
      };
    });
    
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);
    
    const colWidths = [
      { wch: 12 }, // Date
      { wch: 20 }, // Employee Name
      { wch: 20 }, // Department
      { wch: 20 }, // Campaign
      { wch: 15 }, // Clock In
      { wch: 15 }, // Clock Out
      { wch: 15 }, // Work Time
      { wch: 15 }, // Break Time
      { wch: 15 }  // Lunch Time
    ];
    ws['!cols'] = colWidths;
    
    XLSX.utils.book_append_sheet(wb, ws, 'Daily Summary');
    XLSX.writeFile(wb, `Daily_Summary_Report_${getDateString()}.xlsx`);
    
    console.log('✅ Daily Summary Excel export successful');
  } catch (error) {
    console.error('❌ Daily Summary Excel export failed:', error);
    alert(`Failed to export Excel: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const exportDailySummaryToPDF = (data: any[], filters: any) => {
  try {
    const doc = new jsPDF('l', 'mm', 'a4');
    
    doc.setFontSize(16);
    doc.text('Daily Summary Report', 14, 15);
    
    doc.setFontSize(10);
    let yPosition = 25;
    
    if (filters.startDate || filters.endDate) {
      const dateRange = `${filters.startDate || 'Start'} to ${filters.endDate || 'End'}`;
      doc.text(`Date Range: ${dateRange}`, 14, yPosition);
      yPosition += 5;
    }
    
    doc.text(`Total Records: ${data.length}`, 14, yPosition);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, yPosition + 5);
    
    const tableData = data.map(entry => {
      // Handle user name - could be nested in user object or flat
      let employeeName = '';
      if (entry.user) {
        if (typeof entry.user === 'string') {
          employeeName = entry.user;
        } else {
          employeeName = `${entry.user.firstName || ''} ${entry.user.lastName || ''}`.trim();
        }
      } else if (entry.employeeName) {
        employeeName = entry.employeeName;
      }
      
      // Handle department - could be nested or flat
      let department = 'N/A';
      if (entry.department) {
        department = typeof entry.department === 'string' ? entry.department : entry.department.name || 'N/A';
      } else if (entry.user?.department) {
        department = typeof entry.user.department === 'string' ? entry.user.department : entry.user.department.name || 'N/A';
      }
      
      // Handle campaign name - could be object or string
      let campaignName = 'N/A';
      if (entry.campaign) {
        if (typeof entry.campaign === 'string') {
          campaignName = entry.campaign;
        } else if (entry.campaign.name) {
          campaignName = entry.campaign.name;
        }
      }
      
      // Use formatted times if available, otherwise format raw timestamps
      let clockIn = entry.clockInFormatted || 'N/A';
      if (!clockIn || clockIn === 'N/A') {
        if (entry.clockIn) {
          clockIn = moment(entry.clockIn).format('hh:mm A');
        }
      }
      
      let clockOut = entry.clockOutFormatted || 'N/A';
      if (!clockOut || clockOut === 'N/A') {
        if (entry.clockOut) {
          clockOut = moment(entry.clockOut).format('hh:mm A');
        }
      }
      
      // Use totalWorkTime, fallback to totalHours
      const workTime = entry.totalWorkTime || entry.totalHours || 0;
      
      return [
        entry.date || '',
        employeeName || 'N/A',
        department,
        campaignName,
        clockIn,
        clockOut,
        workTime,
        entry.totalBreakTime || 0,
        entry.totalLunchTime || 0
      ];
    });
    
    autoTable(doc, {
      head: [['Date', 'Employee Name', 'Department', 'Campaign', 'Clock In', 'Clock Out', 'Work Time (h)', 'Break Time (m)', 'Lunch Time (m)']],
      body: tableData,
      startY: yPosition + 15,
      styles: {
        fontSize: 7,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { cellWidth: 15 }, // Date
        1: { cellWidth: 25 }, // Employee Name
        2: { cellWidth: 20 }, // Department
        3: { cellWidth: 20 }, // Campaign
        4: { cellWidth: 15 }, // Clock In
        5: { cellWidth: 15 }, // Clock Out
        6: { cellWidth: 15 }, // Work Time
        7: { cellWidth: 15 }, // Break Time
        8: { cellWidth: 15 }, // Lunch Time
      },
      margin: { left: 14, right: 14 },
    });
    
    doc.save(`Daily_Summary_Report_${getDateString()}.pdf`);
    console.log('✅ Daily Summary PDF export successful');
  } catch (error) {
    console.error('❌ Daily Summary PDF export failed:', error);
    alert(`Failed to export PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

// Campaign Performance Export Functions
export const exportCampaignPerformanceToCSV = (data: any[], filters: any) => {
  try {
    const headers = [
      'Campaign',
      'Department',
      'Total Employees',
      'Clock Ins',
      'Clock Outs',
      'Avg Work Time (h)',
      'Attendance Rate (%)',
      'Productivity Score (%)'
    ];
    
    const csvData = data.map(entry => {
      // Handle campaign name - could be object or string
      let campaignName = 'N/A';
      if (entry.campaign) {
        if (typeof entry.campaign === 'string') {
          campaignName = entry.campaign;
        } else if (entry.campaign.name) {
          campaignName = entry.campaign.name;
        }
      }
      
      return [
        campaignName,
        entry.department || 'N/A',
        entry.totalEmployees || 0,
        entry.totalClockIns || 0,
        entry.totalClockOuts || 0,
        entry.averageWorkTime || 0,
        entry.attendanceRate || 0,
        entry.productivityScore || 0
      ];
    });
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Campaign_Performance_Report_${getDateString()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    console.log('✅ Campaign Performance CSV export successful');
  } catch (error) {
    console.error('❌ Campaign Performance CSV export failed:', error);
    alert(`Failed to export CSV: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const exportCampaignPerformanceToExcel = (data: any[], filters: any) => {
  try {
    const excelData = data.map(entry => {
      // Handle campaign name - could be object or string
      let campaignName = 'N/A';
      if (entry.campaign) {
        if (typeof entry.campaign === 'string') {
          campaignName = entry.campaign;
        } else if (entry.campaign.name) {
          campaignName = entry.campaign.name;
        }
      }
      
      return {
        'Campaign': campaignName,
        'Department': entry.department || 'N/A',
        'Total Employees': entry.totalEmployees || 0,
        'Clock Ins': entry.totalClockIns || 0,
        'Clock Outs': entry.totalClockOuts || 0,
        'Avg Work Time (h)': entry.averageWorkTime || 0,
        'Attendance Rate (%)': entry.attendanceRate || 0,
        'Productivity Score (%)': entry.productivityScore || 0
      };
    });
    
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);
    
    const colWidths = [
      { wch: 25 }, // Campaign
      { wch: 20 }, // Department
      { wch: 15 }, // Total Employees
      { wch: 12 }, // Clock Ins
      { wch: 12 }, // Clock Outs
      { wch: 18 }, // Avg Work Time
      { wch: 18 }, // Attendance Rate
      { wch: 20 }  // Productivity Score
    ];
    ws['!cols'] = colWidths;
    
    XLSX.utils.book_append_sheet(wb, ws, 'Campaign Performance');
    XLSX.writeFile(wb, `Campaign_Performance_Report_${getDateString()}.xlsx`);
    
    console.log('✅ Campaign Performance Excel export successful');
  } catch (error) {
    console.error('❌ Campaign Performance Excel export failed:', error);
    alert(`Failed to export Excel: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const exportCampaignPerformanceToPDF = (data: any[], filters: any) => {
  try {
    const doc = new jsPDF('l', 'mm', 'a4');
    
    doc.setFontSize(16);
    doc.text('Campaign Performance Report', 14, 15);
    
    doc.setFontSize(10);
    let yPosition = 25;
    
    if (filters.startDate || filters.endDate) {
      const dateRange = `${filters.startDate || 'Start'} to ${filters.endDate || 'End'}`;
      doc.text(`Date Range: ${dateRange}`, 14, yPosition);
      yPosition += 5;
    }
    
    doc.text(`Total Records: ${data.length}`, 14, yPosition);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, yPosition + 5);
    
    const tableData = data.map(entry => {
      // Handle campaign name - could be object or string
      let campaignName = 'N/A';
      if (entry.campaign) {
        if (typeof entry.campaign === 'string') {
          campaignName = entry.campaign;
        } else if (entry.campaign.name) {
          campaignName = entry.campaign.name;
        }
      }
      
      return [
        campaignName,
        entry.department || 'N/A',
        entry.totalEmployees || 0,
        entry.totalClockIns || 0,
        entry.totalClockOuts || 0,
        entry.averageWorkTime || 0,
        entry.attendanceRate || 0,
        entry.productivityScore || 0
      ];
    });
    
    autoTable(doc, {
      head: [['Campaign', 'Department', 'Total Employees', 'Clock Ins', 'Clock Outs', 'Avg Work Time (h)', 'Attendance Rate (%)', 'Productivity Score (%)']],
      body: tableData,
      startY: yPosition + 15,
      styles: {
        fontSize: 7,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { cellWidth: 25 }, // Campaign
        1: { cellWidth: 20 }, // Department
        2: { cellWidth: 15 }, // Total Employees
        3: { cellWidth: 12 }, // Clock Ins
        4: { cellWidth: 12 }, // Clock Outs
        5: { cellWidth: 18 }, // Avg Work Time
        6: { cellWidth: 18 }, // Attendance Rate
        7: { cellWidth: 20 }, // Productivity Score
      },
      margin: { left: 14, right: 14 },
    });
    
    doc.save(`Campaign_Performance_Report_${getDateString()}.pdf`);
    console.log('✅ Campaign Performance PDF export successful');
  } catch (error) {
    console.error('❌ Campaign Performance PDF export failed:', error);
    alert(`Failed to export PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

// Employee Attendance Export Functions
export const exportEmployeeAttendanceToCSV = (data: any[], filters: any) => {
  try {
    const headers = [
      'Employee ID',
      'Employee Name',
      'Department',
      'Status',
      'Role',
      'Total Days',
      'Clock Ins',
      'Clock Outs',
      'Avg Work Time (h)',
      'Total Hours Worked (h)',
      'Attendance Rate (%)',
      'Punctuality Score (%)',
      'Last Activity'
    ];
    
    const csvData = data.map(entry => [
      entry.user?.employeeId || '',
      `${entry.user?.firstName || ''} ${entry.user?.lastName || ''}`.trim(),
      entry.department || 'N/A',
      entry.assignmentStatus || 'Unassigned',
      entry.user?.role || '',
      entry.totalDays || 0,
      entry.totalClockIns || 0,
      entry.totalClockOuts || 0,
      entry.averageWorkTime || 0,
      entry.totalWorkTime || 0,
      entry.attendanceRate || 0,
      entry.punctualityScore || 0,
      entry.lastActivityFormatted || 'N/A'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Employee_Attendance_Report_${getDateString()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    console.log('✅ Employee Attendance CSV export successful');
  } catch (error) {
    console.error('❌ Employee Attendance CSV export failed:', error);
    alert(`Failed to export CSV: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const exportEmployeeAttendanceToExcel = (data: any[], filters: any) => {
  try {
    const excelData = data.map(entry => ({
      'Employee ID': entry.user?.employeeId || '',
      'Employee Name': `${entry.user?.firstName || ''} ${entry.user?.lastName || ''}`.trim(),
      'Department': entry.department || 'N/A',
      'Status': entry.assignmentStatus || 'Unassigned',
      'Role': entry.user?.role || '',
      'Total Days': entry.totalDays || 0,
      'Clock Ins': entry.totalClockIns || 0,
      'Clock Outs': entry.totalClockOuts || 0,
      'Avg Work Time (h)': entry.averageWorkTime || 0,
      'Total Hours Worked (h)': entry.totalWorkTime || 0,
      'Attendance Rate (%)': entry.attendanceRate || 0,
      'Punctuality Score (%)': entry.punctualityScore || 0,
      'Last Activity': entry.lastActivityFormatted || 'N/A'
    }));
    
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);
    
    const colWidths = [
      { wch: 12 }, // Employee ID
      { wch: 20 }, // Employee Name
      { wch: 20 }, // Department
      { wch: 14 }, // Status
      { wch: 15 }, // Role
      { wch: 12 }, // Total Days
      { wch: 12 }, // Clock Ins
      { wch: 12 }, // Clock Outs
      { wch: 15 }, // Avg Work Time
      { wch: 20 }, // Total Hours Worked
      { wch: 18 }, // Attendance Rate
      { wch: 20 }, // Punctuality Score
      { wch: 20 }  // Last Activity
    ];
    ws['!cols'] = colWidths;
    
    XLSX.utils.book_append_sheet(wb, ws, 'Employee Attendance');
    XLSX.writeFile(wb, `Employee_Attendance_Report_${getDateString()}.xlsx`);
    
    console.log('✅ Employee Attendance Excel export successful');
  } catch (error) {
    console.error('❌ Employee Attendance Excel export failed:', error);
    alert(`Failed to export Excel: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const exportEmployeeAttendanceToPDF = (data: any[], filters: any) => {
  try {
    const doc = new jsPDF('l', 'mm', 'a4');
    
    doc.setFontSize(16);
    doc.text('Employee Attendance Report', 14, 15);
    
    doc.setFontSize(10);
    let yPosition = 25;
    
    if (filters.startDate || filters.endDate) {
      const dateRange = `${filters.startDate || 'Start'} to ${filters.endDate || 'End'}`;
      doc.text(`Date Range: ${dateRange}`, 14, yPosition);
      yPosition += 5;
    }
    
    doc.text(`Total Records: ${data.length}`, 14, yPosition);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, yPosition + 5);
    
    const tableData = data.map(entry => [
      entry.user?.employeeId || '',
      `${entry.user?.firstName || ''} ${entry.user?.lastName || ''}`.trim(),
      entry.department || 'N/A',
      entry.assignmentStatus || 'Unassigned',
      entry.user?.role || '',
      entry.totalDays || 0,
      entry.totalClockIns || 0,
      entry.totalClockOuts || 0,
      entry.averageWorkTime || 0,
      entry.totalWorkTime || 0,
      entry.attendanceRate || 0,
      entry.punctualityScore || 0,
      entry.lastActivityFormatted || 'N/A'
    ]);
    
    autoTable(doc, {
      head: [['Employee ID', 'Employee Name', 'Department', 'Status', 'Role', 'Total Days', 'Clock Ins', 'Clock Outs', 'Avg Work Time (h)', 'Total Hours Worked (h)', 'Attendance Rate (%)', 'Punctuality Score (%)', 'Last Activity']],
      body: tableData,
      startY: yPosition + 15,
      styles: {
        fontSize: 7,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { cellWidth: 12 }, // Employee ID
        1: { cellWidth: 20 }, // Employee Name
        2: { cellWidth: 18 }, // Department
        3: { cellWidth: 12 }, // Role
        4: { cellWidth: 10 }, // Total Days
        5: { cellWidth: 10 }, // Clock Ins
        6: { cellWidth: 10 }, // Clock Outs
        7: { cellWidth: 12 }, // Avg Work Time
        8: { cellWidth: 18 }, // Total Hours Worked
        9: { cellWidth: 15 }, // Attendance Rate
        10: { cellWidth: 18 }, // Punctuality Score
        11: { cellWidth: 18 }, // Last Activity
      },
      margin: { left: 14, right: 14 },
    });
    
    doc.save(`Employee_Attendance_Report_${getDateString()}.pdf`);
    console.log('✅ Employee Attendance PDF export successful');
  } catch (error) {
    console.error('❌ Employee Attendance PDF export failed:', error);
    alert(`Failed to export PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

// Employee Time Sheet Export Functions
export const exportEmployeeTimeSheetToCSV = (data: any[], filters: any) => {
  try {
    const { headers, rows } = buildEmployeeTimeSheetCsvExport(data, filters?.visibleColumns);

    const csvContent = [headers, ...rows]
      .map(row => row.map((field: string | number) => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Employee_Time_Sheet_${getDateString()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    console.log('✅ Employee Time Sheet CSV export successful');
  } catch (error) {
    console.error('❌ Employee Time Sheet CSV export failed:', error);
    alert(`Failed to export CSV: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const exportEmployeeTimeSheetToExcel = (data: any[], filters: any) => {
  try {
    const excelData = buildEmployeeTimeSheetExcelExport(data, filters?.visibleColumns);

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Employee Time Sheet');
    XLSX.writeFile(workbook, `Employee_Time_Sheet_${getDateString()}.xlsx`);
    console.log('✅ Employee Time Sheet Excel export successful');
  } catch (error) {
    console.error('❌ Employee Time Sheet Excel export failed:', error);
    alert(`Failed to export Excel: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const exportEmployeeTimeSheetToPDF = (data: any[], filters: any) => {
  try {
    const { headers, rows } = buildEmployeeTimeSheetPdfExport(data, filters?.visibleColumns);

    const doc = new jsPDF('landscape');
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Employee Time Sheet', 14, 22);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    let yPos = 30;

    if (filters.startDate || filters.endDate) {
      const dateRange = `${filters.startDate || 'N/A'} to ${filters.endDate || 'N/A'}`;
      doc.text(`Date Range: ${dateRange}`, 14, yPos);
      yPos += 6;
    }

    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, yPos);
    yPos += 10;

    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: yPos,
      styles: {
        fontSize: 8,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
        fontStyle: 'bold',
      },
      margin: { left: 14, right: 14 },
    });

    doc.save(`Employee_Time_Sheet_${getDateString()}.pdf`);
    console.log('✅ Employee Time Sheet PDF export successful');
  } catch (error) {
    console.error('❌ Employee Time Sheet PDF export failed:', error);
    alert(`Failed to export PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

const formatMinutes = (minutes?: number) => {
  const totalMinutes = Math.round(minutes || 0);
  const hrs = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${hrs}h ${mins}m`;
};

const toWholeMinutes = (minutes?: number) => Math.round(minutes || 0);
const toMinutesFromHours = (hours?: number) => Math.round((hours || 0) * 60);

// Absence Report Export Functions
export const exportAbsenceToCSV = (data: any[], filters: any) => {
  try {
    const headers = [
      'Employee Name',
      'Employee ID',
      'Absence Type',
      'Start Date',
      'End Date',
      'Duration (Days)',
      'Status',
      'Reason',
      'Approved By',
      'Created Date'
    ];

    const csvData = data.map(absence => [
      `${absence.user.firstName} ${absence.user.lastName}`,
      absence.user.employeeId,
      formatAbsenceTypeForExport(absence),
      formatAbsenceDate(absence.startDate, absence.user?.timezone),
      formatAbsenceDate(absence.endDate, absence.user?.timezone),
      calculateDuration(absence.startDate, absence.endDate),
      absence.status.charAt(0).toUpperCase() + absence.status.slice(1),
      absence.reason || '',
      absence.approvedByUser ? `${absence.approvedByUser.firstName} ${absence.approvedByUser.lastName}` : '',
      TimezoneService.formatDateTimeConsistent(absence.createdAt, 'UTC')
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Absence_Report_${getDateString()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    console.log('✅ Absence CSV export successful');
  } catch (error) {
    console.error('❌ Absence CSV export failed:', error);
    alert(`Failed to export CSV: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const exportAbsenceToExcel = (data: any[], filters: any) => {
  try {
    const headers = [
      'Employee Name',
      'Employee ID',
      'Absence Type',
      'Start Date',
      'End Date',
      'Duration (Days)',
      'Status',
      'Reason',
      'Approved By',
      'Created Date'
    ];

    const excelData = data.map(absence => [
      `${absence.user.firstName} ${absence.user.lastName}`,
      absence.user.employeeId,
      formatAbsenceTypeForExport(absence),
      formatAbsenceDate(absence.startDate, absence.user?.timezone),
      formatAbsenceDate(absence.endDate, absence.user?.timezone),
      calculateDuration(absence.startDate, absence.endDate),
      absence.status.charAt(0).toUpperCase() + absence.status.slice(1),
      absence.reason || '',
      absence.approvedByUser ? `${absence.approvedByUser.firstName} ${absence.approvedByUser.lastName}` : '',
      TimezoneService.formatDateTimeConsistent(absence.createdAt, 'UTC')
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...excelData]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Absence Report');

    // Set column widths
    const columnWidths = [
      { wch: 20 }, // Employee Name
      { wch: 15 }, // Employee ID
      { wch: 15 }, // Absence Type
      { wch: 12 }, // Start Date
      { wch: 12 }, // End Date
      { wch: 15 }, // Duration
      { wch: 12 }, // Status
      { wch: 30 }, // Reason
      { wch: 20 }, // Approved By
      { wch: 20 }, // Created Date
    ];
    worksheet['!cols'] = columnWidths;

    XLSX.writeFile(workbook, `Absence_Report_${getDateString()}.xlsx`);
    console.log('✅ Absence Excel export successful');
  } catch (error) {
    console.error('❌ Absence Excel export failed:', error);
    alert(`Failed to export Excel: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const exportAbsenceToPDF = (data: any[], filters: any) => {
  try {
    const doc = new jsPDF('landscape');
    
    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Absence Report', 14, 22);
    
    // Filters info
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    let yPos = 30;
    
    if (filters.startDate || filters.endDate) {
      const dateRange = `${filters.startDate || 'N/A'} to ${filters.endDate || 'N/A'}`;
      doc.text(`Date Range: ${dateRange}`, 14, yPos);
      yPos += 6;
    }
    
    if (filters.absenceType) {
      doc.text(`Absence Type: ${filters.absenceType}`, 14, yPos);
      yPos += 6;
    }
    
    if (filters.status) {
      doc.text(`Status: ${filters.status}`, 14, yPos);
      yPos += 6;
    }
    
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, yPos);
    yPos += 10;

    // Table data
    const tableData = data.map(absence => [
      `${absence.user.firstName} ${absence.user.lastName}`,
      absence.user.employeeId,
      formatAbsenceTypeForExport(absence),
      formatAbsenceDate(absence.startDate, absence.user?.timezone),
      formatAbsenceDate(absence.endDate, absence.user?.timezone),
      calculateDuration(absence.startDate, absence.endDate).toString(),
      absence.status.charAt(0).toUpperCase() + absence.status.slice(1),
      absence.reason || '',
      absence.approvedByUser ? `${absence.approvedByUser.firstName} ${absence.approvedByUser.lastName}` : '',
      TimezoneService.formatDateTimeConsistent(absence.createdAt, 'UTC')
    ]);

    autoTable(doc, {
      head: [['Employee', 'ID', 'Type', 'Start Date', 'End Date', 'Days', 'Status', 'Reason', 'Approved By', 'Created']],
      body: tableData,
      startY: yPos,
      styles: {
        fontSize: 8,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { cellWidth: 20 }, // Employee
        1: { cellWidth: 15 }, // ID
        2: { cellWidth: 15 }, // Type
        3: { cellWidth: 15 }, // Start Date
        4: { cellWidth: 15 }, // End Date
        5: { cellWidth: 10 }, // Days
        6: { cellWidth: 12 }, // Status
        7: { cellWidth: 25 }, // Reason
        8: { cellWidth: 20 }, // Approved By
        9: { cellWidth: 20 }, // Created
      },
      margin: { left: 14, right: 14 },
    });
    
    doc.save(`Absence_Report_${getDateString()}.pdf`);
    console.log('✅ Absence PDF export successful');
  } catch (error) {
    console.error('❌ Absence PDF export failed:', error);
    alert(`Failed to export PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

// Helper function for calculating duration
const calculateDuration = (startDate: string, endDate: string) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  return diffDays;
};

const formatAbsenceDate = (dateString: string, timezone?: string) => {
  if (!dateString) return '';
  const tz = timezone || TimezoneService.getUserTimezone() || 'UTC';
  if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return moment.tz(dateString, 'YYYY-MM-DD', tz).format('MM/DD/YYYY');
  }
  if (dateString.includes('T') || dateString.includes(' ')) {
    return moment.utc(dateString).tz(tz).format('MM/DD/YYYY');
  }
  return moment.tz(dateString, tz).format('MM/DD/YYYY');
};

// Tardies Report Export Functions
export const exportTardiesToCSV = (data: any[], filters: any) => {
  try {
    const headers = [
      'Employee Name',
      'Employee ID',
      'Date',
      'Scheduled Start Time',
      'Actual Clock-In Time',
      'Minutes Late',
      'Status',
      'Department',
      'Campaign'
    ];

    const csvData = data.map(tardy => [
      tardy.employeeName,
      tardy.employeeId,
      tardy.date,
      tardy.scheduledStartTime,
      tardy.actualClockInTime,
      tardy.minutesLate,
      tardy.status === 'late' ? 'Late' : 'On-Time',
      tardy.department,
      tardy.campaignName
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Tardies_Report_${getDateString()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    console.log('✅ Tardies CSV export successful');
  } catch (error) {
    console.error('❌ Tardies CSV export failed:', error);
    alert(`Failed to export CSV: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const exportTardiesToExcel = (data: any[], filters: any) => {
  try {
    const headers = [
      'Employee Name',
      'Employee ID',
      'Date',
      'Scheduled Start Time',
      'Actual Clock-In Time',
      'Minutes Late',
      'Status',
      'Department',
      'Campaign'
    ];

    const excelData = data.map(tardy => [
      tardy.employeeName,
      tardy.employeeId,
      tardy.date,
      tardy.scheduledStartTime,
      tardy.actualClockInTime,
      tardy.minutesLate,
      tardy.status === 'late' ? 'Late' : 'On-Time',
      tardy.department,
      tardy.campaignName
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...excelData]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Tardies Report');

    // Set column widths
    const columnWidths = [
      { wch: 20 }, // Employee Name
      { wch: 15 }, // Employee ID
      { wch: 12 }, // Date
      { wch: 20 }, // Scheduled Start Time
      { wch: 20 }, // Actual Clock-In Time
      { wch: 15 }, // Minutes Late
      { wch: 12 }, // Status
      { wch: 20 }, // Department
      { wch: 20 }, // Campaign
    ];
    worksheet['!cols'] = columnWidths;

    XLSX.writeFile(workbook, `Tardies_Report_${getDateString()}.xlsx`);
    console.log('✅ Tardies Excel export successful');
  } catch (error) {
    console.error('❌ Tardies Excel export failed:', error);
    alert(`Failed to export Excel: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const exportTardiesToPDF = (data: any[], filters: any) => {
  try {
    const doc = new jsPDF('landscape');
    
    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Tardies Report', 14, 22);
    
    // Filters info
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    let yPos = 30;
    
    if (filters.startDate || filters.endDate) {
      const dateRange = `${filters.startDate || 'N/A'} to ${filters.endDate || 'N/A'}`;
      doc.text(`Date Range: ${dateRange}`, 14, yPos);
      yPos += 6;
    }
    
    if (filters.status && filters.status !== 'all') {
      doc.text(`Status: ${filters.status === 'late' ? 'Late Only' : 'On-Time Only'}`, 14, yPos);
      yPos += 6;
    }
    
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, yPos);
    yPos += 10;

    // Table data
    const tableData = data.map(tardy => [
      tardy.employeeName,
      tardy.employeeId,
      tardy.date,
      tardy.scheduledStartTime,
      tardy.actualClockInTime,
      tardy.minutesLate.toString(),
      tardy.status === 'late' ? 'Late' : 'On-Time',
      tardy.department,
      tardy.campaignName
    ]);

    autoTable(doc, {
      head: [['Employee', 'ID', 'Date', 'Scheduled', 'Actual Clock-In', 'Minutes Late', 'Status', 'Department', 'Campaign']],
      body: tableData,
      startY: yPos,
      styles: {
        fontSize: 8,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { cellWidth: 20 }, // Employee
        1: { cellWidth: 15 }, // ID
        2: { cellWidth: 12 }, // Date
        3: { cellWidth: 18 }, // Scheduled
        4: { cellWidth: 18 }, // Actual Clock-In
        5: { cellWidth: 15 }, // Minutes Late
        6: { cellWidth: 12 }, // Status
        7: { cellWidth: 20 }, // Department
        8: { cellWidth: 20 }, // Campaign
      },
      margin: { left: 14, right: 14 },
    });
    
    doc.save(`Tardies_Report_${getDateString()}.pdf`);
    console.log('✅ Tardies PDF export successful');
  } catch (error) {
    console.error('❌ Tardies PDF export failed:', error);
    alert(`Failed to export PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

// Validation Report Export Functions
export const exportValidationReportToCSV = (data: any[], filters: any) => {
  try {
    const headers = [
      'Employee Name',
      'Employee ID',
      'Date',
      'Issue Type',
      'Message',
      'Expected Entry',
      'Actual Entry',
      'Severity',
      'Total Issues',
      'Total Entries'
    ];

    const rows = data.flatMap(result => 
      result.issues.map((issue: any) => [
        result.employeeName || '',
        result.employeeId || '',
        result.date || '',
        issue.type || '',
        issue.message || '',
        issue.expectedEntry || '',
        issue.actualEntry || '',
        issue.severity || '',
        result.issues.length || 0,
        result.totalEntries || 0
      ])
    );

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map((cell: any) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `time-entry-validation-report_${getDateString()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    console.log('✅ Validation Report CSV export successful');
  } catch (error) {
    console.error('❌ Validation Report CSV export failed:', error);
    alert(`Failed to export CSV: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const exportValidationReportToExcel = (data: any[], filters: any) => {
  try {
    const headers = [
      'Employee Name',
      'Employee ID',
      'Date',
      'Issue Type',
      'Message',
      'Expected Entry',
      'Actual Entry',
      'Severity',
      'Total Issues',
      'Total Entries'
    ];

    const worksheetData = [headers];
    
    data.forEach(result => {
      if (result.issues && result.issues.length > 0) {
        result.issues.forEach((issue: any) => {
          worksheetData.push([
            result.employeeName || '',
            result.employeeId || '',
            result.date || '',
            issue.type || '',
            issue.message || '',
            issue.expectedEntry || '',
            issue.actualEntry || '',
            issue.severity || '',
            result.issues.length || 0,
            result.totalEntries || 0
          ]);
        });
      } else {
        worksheetData.push([
          result.employeeName || '',
          result.employeeId || '',
          result.date || '',
          '',
          '',
          '',
          '',
          result.severity || '',
          result.issues.length || 0,
          result.totalEntries || 0
        ]);
      }
    });
    
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Validation Report');
    XLSX.writeFile(workbook, `time-entry-validation-report_${getDateString()}.xlsx`);
    
    console.log('✅ Validation Report Excel export successful');
  } catch (error) {
    console.error('❌ Validation Report Excel export failed:', error);
    alert(`Failed to export Excel: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const exportValidationReportToPDF = (data: any[], filters: any) => {
  try {
    const doc = new jsPDF('landscape');
    
    doc.setFontSize(16);
    doc.text('Time Entry Validation Report', 14, 15);
    
    doc.setFontSize(10);
    let yPos = 25;
    if (filters.startDate && filters.endDate) {
      doc.text(`Date Range: ${filters.startDate} to ${filters.endDate}`, 14, yPos);
      yPos += 5;
    }
    if (filters.severity && filters.severity !== 'all') {
      doc.text(`Severity: ${filters.severity}`, 14, yPos);
      yPos += 5;
    }
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, yPos);
    yPos += 10;
    
    const tableData: any[] = [];
    
    data.forEach(result => {
      if (result.issues && result.issues.length > 0) {
        result.issues.forEach((issue: any) => {
          tableData.push([
            result.employeeName || '',
            result.employeeId || '',
            result.date || '',
            issue.type || '',
            (issue.message || '').substring(0, 40),
            issue.expectedEntry || '',
            issue.actualEntry || '',
            issue.severity || ''
          ]);
        });
      }
    });
    
    if (tableData.length > 0) {
      autoTable(doc, {
        head: [['Employee Name', 'Emp ID', 'Date', 'Issue Type', 'Message', 'Expected', 'Actual', 'Severity']],
        body: tableData,
        startY: yPos,
        styles: { fontSize: 7 },
        headStyles: { fillColor: [66, 139, 202] },
        columnStyles: {
          0: { cellWidth: 35 },
          1: { cellWidth: 20 },
          2: { cellWidth: 25 },
          3: { cellWidth: 30 },
          4: { cellWidth: 50 },
          5: { cellWidth: 25 },
          6: { cellWidth: 25 },
          7: { cellWidth: 20 },
        },
        margin: { left: 14, right: 14 },
      });
    } else {
      doc.text('No validation issues found', 14, yPos);
    }
    
    doc.save(`time-entry-validation-report_${getDateString()}.pdf`);
    console.log('✅ Validation Report PDF export successful');
  } catch (error) {
    console.error('❌ Validation Report PDF export failed:', error);
    alert(`Failed to export PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

// Weekly Attendance Report Export Functions
export const exportWeeklyAttendanceToCSV = (reportData: any, dateRange: { startDate: string; endDate: string }) => {
  try {
    const headers = [
      'Employee Name',
      'Employee ID',
      'Week Start',
      'Week End',
      'Day',
      'Date',
      'Clock In',
      'Clock Out',
      'Break Start',
      'Break End',
      'Lunch Start',
      'Lunch End',
      'Total Hours',
      'Campaign'
    ];
    
    const csvRows: string[] = [];
    const seenDates = new Set<string>(); // Track unique employee+date combinations to prevent duplicates
    
    // Flatten the nested structure
    Object.keys(reportData).forEach(userId => {
      const employee = reportData[userId];
      
      Object.keys(employee.weeks).forEach(weekKey => {
        const week = employee.weeks[weekKey];
        
        Object.keys(week.days).forEach(dateKey => {
          const day = week.days[dateKey];
          
          // Create unique key for this employee+date combination
          const uniqueKey = `${userId}-${dateKey}`;
          
          // Only create a row if:
          // 1. We haven't seen this employee+date combination before
          // 2. There's consolidated day data (clockIn, breakStart, or lunchStart)
          if (!seenDates.has(uniqueKey) && (day.clockIn || day.breakStart || day.lunchStart)) {
            seenDates.add(uniqueKey);
            csvRows.push([
              employee.name || '',
              employee.employeeId || '',
              week.weekStart || '',
              week.weekEnd || '',
              day.dayOfWeek || '',
              day.date || dateKey || '',
              day.clockIn || '-',
              day.clockOut || '-',
              day.breakStart || '-',
              day.breakEnd || '-',
              day.lunchStart || '-',
              day.lunchEnd || '-',
              day.dailyTotal || '0:00',
              '' // Campaign from entry if available
            ].map(field => `"${field}"`).join(','));
          }
        });
      });
    });
    
    const csvContent = [headers.join(','), ...csvRows].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `weekly-attendance-report-${dateRange.startDate}-to-${dateRange.endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    console.log('✅ Weekly Attendance CSV export successful');
  } catch (error) {
    console.error('❌ Weekly Attendance CSV export failed:', error);
    alert(`Failed to export CSV: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const exportWeeklyAttendanceToExcel = (reportData: any, dateRange: { startDate: string; endDate: string }) => {
  try {
    const excelRows: any[] = [];
    const seenDates = new Set<string>(); // Track unique employee+date combinations to prevent duplicates
    
    // Flatten the nested structure
    Object.keys(reportData).forEach(userId => {
      const employee = reportData[userId];
      
      Object.keys(employee.weeks).forEach(weekKey => {
        const week = employee.weeks[weekKey];
        
        Object.keys(week.days).forEach(dateKey => {
          const day = week.days[dateKey];
          
          // Create unique key for this employee+date combination
          const uniqueKey = `${userId}-${dateKey}`;
          
          // Only create a row if:
          // 1. We haven't seen this employee+date combination before
          // 2. There's consolidated day data (clockIn, breakStart, or lunchStart)
          if (!seenDates.has(uniqueKey) && (day.clockIn || day.breakStart || day.lunchStart)) {
            seenDates.add(uniqueKey);
            excelRows.push({
              'Employee Name': employee.name || '',
              'Employee ID': employee.employeeId || '',
              'Week Start': week.weekStart || '',
              'Week End': week.weekEnd || '',
              'Day': day.dayOfWeek || '',
              'Date': day.date || dateKey || '',
              'Clock In': day.clockIn || '-',
              'Clock Out': day.clockOut || '-',
              'Break Start': day.breakStart || '-',
              'Break End': day.breakEnd || '-',
              'Lunch Start': day.lunchStart || '-',
              'Lunch End': day.lunchEnd || '-',
              'Total Hours': day.dailyTotal || '0:00',
              'Campaign': ''
            });
          }
        });
      });
    });
    
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelRows);
    
    const colWidths = [
      { wch: 20 }, // Employee Name
      { wch: 12 }, // Employee ID
      { wch: 12 }, // Week Start
      { wch: 12 }, // Week End
      { wch: 12 }, // Day
      { wch: 12 }, // Date
      { wch: 15 }, // Clock In
      { wch: 15 }, // Clock Out
      { wch: 15 }, // Break Start
      { wch: 15 }, // Break End
      { wch: 15 }, // Lunch Start
      { wch: 15 }, // Lunch End
      { wch: 12 }, // Total Hours
      { wch: 20 }  // Campaign
    ];
    ws['!cols'] = colWidths;
    
    XLSX.utils.book_append_sheet(wb, ws, 'Weekly Attendance');
    XLSX.writeFile(wb, `weekly-attendance-report-${dateRange.startDate}-to-${dateRange.endDate}.xlsx`);
    
    console.log('✅ Weekly Attendance Excel export successful');
  } catch (error) {
    console.error('❌ Weekly Attendance Excel export failed:', error);
    alert(`Failed to export Excel: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const exportWeeklyAttendanceToPDF = (reportData: any, dateRange: { startDate: string; endDate: string }) => {
  try {
    const doc = new jsPDF('l', 'mm', 'a4');
    
    doc.setFontSize(16);
    doc.text('Weekly Attendance Report', 14, 15);
    
    doc.setFontSize(10);
    let yPosition = 25;
    
    doc.text(`Date Range: ${dateRange.startDate} to ${dateRange.endDate}`, 14, yPosition);
    yPosition += 5;
    doc.text(`Total Employees: ${Object.keys(reportData).length}`, 14, yPosition);
    yPosition += 5;
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, yPosition);
    yPosition += 15;
    
    // Flatten data for table
    const tableData: string[][] = [];
    const seenDates = new Set<string>(); // Track unique employee+date combinations to prevent duplicates
    
    Object.keys(reportData).forEach(userId => {
      const employee = reportData[userId];
      
      Object.keys(employee.weeks).forEach(weekKey => {
        const week = employee.weeks[weekKey];
        
        Object.keys(week.days).forEach(dateKey => {
          const day = week.days[dateKey];
          
          // Create unique key for this employee+date combination
          const uniqueKey = `${userId}-${dateKey}`;
          
          // Only create a row if:
          // 1. We haven't seen this employee+date combination before
          // 2. There's consolidated day data (clockIn, breakStart, or lunchStart)
          if (!seenDates.has(uniqueKey) && (day.clockIn || day.breakStart || day.lunchStart)) {
            seenDates.add(uniqueKey);
            tableData.push([
              employee.name || '',
              employee.employeeId || '',
              week.weekStart || '',
              day.date || dateKey || '',
              day.clockIn || '-',
              day.clockOut || '-',
              day.breakStart || '-',
              day.breakEnd || '-',
              day.lunchStart || '-',
              day.lunchEnd || '-',
              day.dailyTotal || '0:00'
            ]);
          }
        });
      });
    });
    
    autoTable(doc, {
      head: [['Employee', 'ID', 'Week Start', 'Date', 'Clock In', 'Clock Out', 'Break Start', 'Break End', 'Lunch Start', 'Lunch End', 'Hours']],
      body: tableData,
      startY: yPosition,
      styles: {
        fontSize: 7,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { cellWidth: 20 }, // Employee
        1: { cellWidth: 12 }, // ID
        2: { cellWidth: 15 }, // Week Start
        3: { cellWidth: 15 }, // Date
        4: { cellWidth: 15 }, // Clock In
        5: { cellWidth: 15 }, // Clock Out
        6: { cellWidth: 15 }, // Break Start
        7: { cellWidth: 15 }, // Break End
        8: { cellWidth: 15 }, // Lunch Start
        9: { cellWidth: 15 }, // Lunch End
        10: { cellWidth: 12 }, // Hours
      },
      margin: { left: 14, right: 14 },
    });
    
    doc.save(`weekly-attendance-report-${dateRange.startDate}-to-${dateRange.endDate}.pdf`);
    console.log('✅ Weekly Attendance PDF export successful');
  } catch (error) {
    console.error('❌ Weekly Attendance PDF export failed:', error);
    alert(`Failed to export PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

// Scheduled Employees Report Export Functions
export const exportScheduledEmployeesToCSV = (data: any[], selectedDate: string) => {
  try {
    const headers = [
      'Employee Name',
      'Email',
      'Department',
      'Schedule',
      'Campaign',
      'Status',
      'Incident Description',
      'Clock In Time',
      'Clock Out Time'
    ];
    
    const csvData = data.map(employee => [
      `${employee.firstName} ${employee.lastName}`,
      employee.email || '',
      employee.department || '',
      `${employee.startTime} - ${employee.endTime}`,
      employee.campaignName || '',
      employee.statusLabel || employee.currentStatus || (employee.hasClockedIn ? 'Working' : 'Scheduled'),
      employee.incidentDescription || '',
      employee.clockInTimeExport
        || employee.clockInTimeFormatted
        || (employee.clockInTime ? new Date(employee.clockInTime).toLocaleString() : 'Not clocked in'),
      employee.clockOutTimeExport
        || employee.clockOutTimeFormatted
        || (employee.clockOutTime ? new Date(employee.clockOutTime).toLocaleString() : 'Not clocked out')
    ]);
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `scheduled-employees-report-${selectedDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    console.log('✅ Scheduled Employees CSV export successful');
  } catch (error) {
    console.error('❌ Scheduled Employees CSV export failed:', error);
    alert(`Failed to export CSV: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const exportScheduledEmployeesToExcel = (data: any[], selectedDate: string) => {
  try {
    const excelData = data.map(employee => ({
      'Employee Name': `${employee.firstName} ${employee.lastName}`,
      'Email': employee.email || '',
      'Department': employee.department || '',
      'Schedule': `${employee.startTime} - ${employee.endTime}`,
      'Campaign': employee.campaignName || '',
      'Status': employee.statusLabel || employee.currentStatus || (employee.hasClockedIn ? 'Working' : 'Scheduled'),
      'Incident Description': employee.incidentDescription || '',
      'Clock In Time': employee.clockInTimeExport
        || employee.clockInTimeFormatted
        || (employee.clockInTime ? new Date(employee.clockInTime).toLocaleString() : 'Not clocked in'),
      'Clock Out Time': employee.clockOutTimeExport
        || employee.clockOutTimeFormatted
        || (employee.clockOutTime ? new Date(employee.clockOutTime).toLocaleString() : 'Not clocked out')
    }));
    
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);
    
    const colWidths = [
      { wch: 20 }, // Employee Name
      { wch: 25 }, // Email
      { wch: 15 }, // Department
      { wch: 20 }, // Schedule
      { wch: 20 }, // Campaign
      { wch: 15 }, // Status
      { wch: 30 }, // Incident Description
      { wch: 20 }, // Clock In Time
      { wch: 20 }  // Clock Out Time
    ];
    ws['!cols'] = colWidths;
    
    XLSX.utils.book_append_sheet(wb, ws, 'Scheduled Employees');
    XLSX.writeFile(wb, `scheduled-employees-report-${selectedDate}.xlsx`);
    
    console.log('✅ Scheduled Employees Excel export successful');
  } catch (error) {
    console.error('❌ Scheduled Employees Excel export failed:', error);
    alert(`Failed to export Excel: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const exportScheduledEmployeesToPDF = (data: any[], selectedDate: string) => {
  try {
    const doc = new jsPDF('l', 'mm', 'a4');
    
    doc.setFontSize(16);
    doc.text('Scheduled Employees Report', 14, 15);
    
    doc.setFontSize(10);
    doc.text(`Date: ${selectedDate}`, 14, 25);
    doc.text(`Total Employees: ${data.length}`, 14, 32);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 39);
    
    const tableData = data.map(employee => [
      `${employee.firstName} ${employee.lastName}`,
      employee.email || '',
      employee.department || '',
      `${employee.startTime} - ${employee.endTime}`,
      employee.campaignName || '',
      employee.statusLabel || employee.currentStatus || (employee.hasClockedIn ? 'Working' : 'Scheduled'),
      employee.incidentDescription || '',
      employee.clockInTimeExport
        || employee.clockInTimeFormatted
        || (employee.clockInTime ? new Date(employee.clockInTime).toLocaleString() : 'Not clocked in'),
      employee.clockOutTimeExport
        || employee.clockOutTimeFormatted
        || (employee.clockOutTime ? new Date(employee.clockOutTime).toLocaleString() : 'Not clocked out')
    ]);
    
    autoTable(doc, {
      head: [['Employee', 'Email', 'Department', 'Schedule', 'Campaign', 'Status', 'Incident Description', 'Clock In', 'Clock Out']],
      body: tableData,
      startY: 50,
      styles: {
        fontSize: 8,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { cellWidth: 24 }, // Employee
        1: { cellWidth: 28 }, // Email
        2: { cellWidth: 16 }, // Department
        3: { cellWidth: 23 }, // Schedule
        4: { cellWidth: 23 }, // Campaign
        5: { cellWidth: 16 }, // Status
        6: { cellWidth: 28 }, // Incident Description
        7: { cellWidth: 23 }, // Clock In
        8: { cellWidth: 23 }, // Clock Out
      },
      margin: { left: 14, right: 14 },
    });
    
    doc.save(`scheduled-employees-report-${selectedDate}.pdf`);
    console.log('✅ Scheduled Employees PDF export successful');
  } catch (error) {
    console.error('❌ Scheduled Employees PDF export failed:', error);
    alert(`Failed to export PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

// Time Records Management Export Functions
interface TimeRecordExportRecord {
  type: 'time_entry' | 'absence';
  userId: string;
  campaignId?: string;
  entryType?: string;
  absenceType?: string;
  timestamp?: string;
  startDate?: string;
  endDate?: string;
  notes?: string;
  reason?: string;
  status?: string;
  user?: any;
  campaign?: any;
}

export const exportTimeRecordsToCSV = (records: TimeRecordExportRecord[], selectedTimezone: string) => {
  try {
    const headers = [
      'Type',
      'Employee ID',
      'Employee Name',
      'Campaign',
      'Entry/Absence Type',
      'Date/Period',
      'Notes/Reason',
      'Status',
      'Last Modified'
    ];
    
    const csvData = records.map(record => {
      const employeeName = record.user 
        ? `${record.user.firstName || ''} ${record.user.lastName || ''}`.trim()
        : '';
      const employeeId = record.user?.employeeId || '';
      const campaignName = record.campaign?.name || (record.campaignId ? 'Unknown' : 'N/A');
      const entryAbsenceType = record.type === 'time_entry' 
        ? formatEntryType(record.entryType || '')
        : formatAbsenceTypeForExport(record);
      const datePeriod = record.type === 'time_entry'
        ? TimezoneService.formatDateTimeConsistent(record.timestamp || '', selectedTimezone)
        : `${record.startDate || ''} - ${record.endDate || ''}`;
      const notesReason = record.type === 'time_entry' ? (record.notes || '') : (record.reason || '');
      const status = record.type === 'time_entry' ? 'N/A' : (record.status || '');
      
      return [
        record.type === 'time_entry' ? 'Time Entry' : 'Absence',
        employeeId,
        employeeName,
        campaignName,
        entryAbsenceType,
        datePeriod,
        notesReason,
        status,
        '' // Last Modified - could be added if needed
      ];
    });
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `time-records-management_${getDateString()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    console.log('✅ Time Records CSV export successful');
  } catch (error) {
    console.error('❌ Time Records CSV export failed:', error);
    alert(`Failed to export CSV: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const exportTimeRecordsToExcel = (records: TimeRecordExportRecord[], selectedTimezone: string) => {
  try {
    const worksheetData = [
      ['Type', 'Employee ID', 'Employee Name', 'Campaign', 'Entry/Absence Type', 'Date/Period', 'Notes/Reason', 'Status']
    ];
    
    records.forEach(record => {
      const employeeName = record.user 
        ? `${record.user.firstName || ''} ${record.user.lastName || ''}`.trim()
        : '';
      const employeeId = record.user?.employeeId || '';
      const campaignName = record.campaign?.name || (record.campaignId ? 'Unknown' : 'N/A');
      const entryAbsenceType = record.type === 'time_entry' 
        ? formatEntryType(record.entryType || '')
        : formatAbsenceTypeForExport(record);
      const datePeriod = record.type === 'time_entry'
        ? TimezoneService.formatDateTimeConsistent(record.timestamp || '', selectedTimezone)
        : `${record.startDate || ''} - ${record.endDate || ''}`;
      const notesReason = record.type === 'time_entry' ? (record.notes || '') : (record.reason || '');
      const status = record.type === 'time_entry' ? 'N/A' : (record.status || '');
      
      worksheetData.push([
        record.type === 'time_entry' ? 'Time Entry' : 'Absence',
        employeeId,
        employeeName,
        campaignName,
        entryAbsenceType,
        datePeriod,
        notesReason,
        status
      ]);
    });
    
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Time Records');
    XLSX.writeFile(workbook, `time-records-management_${getDateString()}.xlsx`);
    
    console.log('✅ Time Records Excel export successful');
  } catch (error) {
    console.error('❌ Time Records Excel export failed:', error);
    alert(`Failed to export Excel: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const exportTimeRecordsToPDF = (records: TimeRecordExportRecord[], selectedTimezone: string) => {
  try {
    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape
    
    doc.setFontSize(16);
    doc.text('Time Records & Absences Management', 14, 15);
    
    doc.setFontSize(10);
    doc.text(`Total Records: ${records.length}`, 14, 25);
    doc.text(`Timezone: ${TimezoneService.getTimezoneDisplayName(selectedTimezone)}`, 14, 30);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 35);
    
    const tableData = records.map(record => {
      const employeeName = record.user 
        ? `${record.user.firstName || ''} ${record.user.lastName || ''}`.trim()
        : '';
      const employeeId = record.user?.employeeId || '';
      const campaignName = record.campaign?.name || (record.campaignId ? 'Unknown' : 'N/A');
      const entryAbsenceType = record.type === 'time_entry' 
        ? formatEntryType(record.entryType || '')
        : formatAbsenceTypeForExport(record);
      const datePeriod = record.type === 'time_entry'
        ? TimezoneService.formatDateTimeConsistent(record.timestamp || '', selectedTimezone)
        : `${record.startDate || ''} - ${record.endDate || ''}`;
      const notesReason = (record.type === 'time_entry' ? (record.notes || '') : (record.reason || '')).substring(0, 30);
      const status = record.type === 'time_entry' ? 'N/A' : (record.status || '');
      
      return [
        record.type === 'time_entry' ? 'Time Entry' : 'Absence',
        employeeId || '',
        employeeName || '',
        campaignName || '',
        entryAbsenceType || '',
        datePeriod || '',
        notesReason || '',
        status || ''
      ];
    });
    
    autoTable(doc, {
      head: [['Type', 'Emp ID', 'Employee Name', 'Campaign', 'Entry/Abs Type', 'Date/Period', 'Notes/Reason', 'Status']],
      body: tableData,
      startY: 40,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [66, 139, 202] },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 20 },
        2: { cellWidth: 35 },
        3: { cellWidth: 30 },
        4: { cellWidth: 30 },
        5: { cellWidth: 40 },
        6: { cellWidth: 40 },
        7: { cellWidth: 25 },
      },
      margin: { left: 14, right: 14 },
    });
    
    doc.save(`time-records-management_${getDateString()}.pdf`);
    console.log('✅ Time Records PDF export successful');
  } catch (error) {
    console.error('❌ Time Records PDF export failed:', error);
    alert(`Failed to export PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};